import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { REPORTSB_API_URL } from "config";

// reports-b "Customise Layout" persistence (per-tenancy, inherited up the
// parent chain then tenancy 0). Backed by the reporting service's
// /dashboard/layout endpoints. Mirrors dashboard.service auth handling.
const baseQuery = fetchBaseQuery({
    baseUrl: REPORTSB_API_URL,
    prepareHeaders: (headers) => {
        const user = localStorage.getItem('user');
        if (user) {
            const token = JSON.parse(user).token;
            if (token) headers.set('authorization', `Bearer ${token}`);
        }
        return headers;
    },
    credentials: "omit"
});

export const reportLayoutApi = createApi({
    reducerPath: "reportLayoutApi",
    baseQuery,
    tagTypes: ['Layout', 'ExportTemplates', 'ExportHistory', 'FlagAcks', 'ExportSchedules'],
    endpoints: (builder) => ({
        // Effective (inheritance-resolved) layout for every page of a tenancy:
        // { tenancyId, pages: { summary|sims|courses: { page, hidden[], templates[], source } } }
        getLayout: builder.query({
            query: (tenancyId) => ({ url: `dashboard/layout/${tenancyId}`, method: 'GET' }),
            providesTags: (result, error, tenancyId) => [{ type: 'Layout', id: tenancyId }],
        }),
        // Save one page's layout for the tenancy. body: { page, hidden[], templates[] }
        saveLayout: builder.mutation({
            query: ({ tenancyId, page, hidden, templates }) => ({
                url: `dashboard/layout/${tenancyId}`,
                method: 'PUT',
                body: { page, hidden, templates },
            }),
            // Refetch for any subscriber (Features page widget switches and the
            // in-page customise drawer share this cache entry).
            invalidatesTags: (result, error, { tenancyId }) => [{ type: 'Layout', id: tenancyId }],
        }),
        // Reset one page back to inherited (removes this tenancy's record).
        resetLayout: builder.mutation({
            query: ({ tenancyId, page }) => ({
                url: `dashboard/layout/${tenancyId}/${page}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { tenancyId }) => [{ type: 'Layout', id: tenancyId }],
        }),
        // The tenancy's saved export-builder templates (tenancy-owned, NOT
        // inherited): { tenancyId, templates: [{ id, name, title, format,
        // period, sections[], drill[] }] }
        getExportTemplates: builder.query({
            query: (tenancyId) => ({ url: `dashboard/export-templates/${tenancyId}`, method: 'GET' }),
            providesTags: (result, error, tenancyId) => [{ type: 'ExportTemplates', id: tenancyId }],
        }),
        // Save the tenancy's template list wholesale (add + delete both send
        // the full next list, mirroring how layout presets persist).
        saveExportTemplates: builder.mutation({
            query: ({ tenancyId, templates }) => ({
                url: `dashboard/export-templates/${tenancyId}`,
                method: 'PUT',
                body: { templates },
            }),
            invalidatesTags: (result, error, { tenancyId }) => [{ type: 'ExportTemplates', id: tenancyId }],
        }),
        // The tenancy's export history (newest first, server-capped at 200):
        // { tenancyId, entries: [{ id, at, by, kind, via, title, format, ... }] }
        getExportHistory: builder.query({
            query: (tenancyId) => ({ url: `dashboard/export-history/${tenancyId}`, method: 'GET' }),
            providesTags: (result, error, tenancyId) => [{ type: 'ExportHistory', id: tenancyId }],
        }),
        // Append one entry; the server stamps id/time/user and trims the list.
        appendExportHistory: builder.mutation({
            query: ({ tenancyId, entry }) => ({
                url: `dashboard/export-history/${tenancyId}`,
                method: 'POST',
                body: entry,
            }),
            invalidatesTags: (result, error, { tenancyId }) => [{ type: 'ExportHistory', id: tenancyId }],
        }),
        // One-off "email a copy": enqueue a render+email of the current export.
        // The BFF puts it on the delivery queue; the sender renders + emails it,
        // reusing the scheduled-delivery pipeline. render = { sections, drill,
        // format, period, startDate, endDate, scope, benchmarkOn, templateId }.
        deliverExportNow: builder.mutation({
            query: ({ tenancyId, recipients, title, render }) => ({
                url: `dashboard/export-deliver-now/${tenancyId}`,
                method: 'POST',
                body: { recipients, title, render },
            }),
            invalidatesTags: (result, error, { tenancyId }) => [{ type: 'ExportHistory', id: tenancyId }],
        }),
        clearExportHistory: builder.mutation({
            query: (tenancyId) => ({ url: `dashboard/export-history/${tenancyId}`, method: 'DELETE' }),
            invalidatesTags: (result, error, tenancyId) => [{ type: 'ExportHistory', id: tenancyId }],
        }),
        // Acknowledged flagged sims (tenancy-wide): { tenancyId, acks: { [simName]: { at } } }.
        // Shared cache entry keeps the Flagged Sims KPI and the drill page in step.
        getFlaggedSimAcks: builder.query({
            query: (tenancyId) => ({ url: `dashboard/flagged-sim-acks/${tenancyId}`, method: 'GET' }),
            providesTags: (result, error, tenancyId) => [{ type: 'FlagAcks', id: tenancyId }],
        }),
        // Automated export schedules (tenancy-owned). Server-side because the
        // delivery job can't see a browser: a schedule is a standing instruction
        // the platform acts on, not a preference of whoever created it.
        getExportSchedules: builder.query({
            query: (tenancyId) => ({ url: `dashboard/export-schedules/${tenancyId}`, method: 'GET' }),
            providesTags: (result, error, tenancyId) => [{ type: 'ExportSchedules', id: tenancyId }],
        }),
        saveExportSchedules: builder.mutation({
            query: ({ tenancyId, schedules }) => ({
                url: `dashboard/export-schedules/${tenancyId}`,
                method: 'PUT',
                body: { schedules },
            }),
            invalidatesTags: (result, error, { tenancyId }) => [{ type: 'ExportSchedules', id: tenancyId }],
        }),
        saveFlaggedSimAcks: builder.mutation({
            query: ({ tenancyId, acks }) => ({
                url: `dashboard/flagged-sim-acks/${tenancyId}`,
                method: 'PUT',
                body: { acks },
            }),
            invalidatesTags: (result, error, { tenancyId }) => [{ type: 'FlagAcks', id: tenancyId }],
        }),
    }),
});

export const {
    useGetLayoutQuery, useSaveLayoutMutation, useResetLayoutMutation,
    useGetExportTemplatesQuery, useSaveExportTemplatesMutation,
    useGetExportHistoryQuery, useAppendExportHistoryMutation, useClearExportHistoryMutation, useDeliverExportNowMutation,
    useGetFlaggedSimAcksQuery, useSaveFlaggedSimAcksMutation,
    useGetExportSchedulesQuery, useSaveExportSchedulesMutation,
} = reportLayoutApi;
